/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Presentation vocabulary for notifications.
 *
 * The inbox list and the notification detail page render the same channels,
 * priorities and delivery outcomes, so the maps live here rather than being
 * copied into each screen — a channel added in one place then shows up in both.
 */
import type { Notification } from '../../types';

export const CHANNEL_LABEL: Record<string, string> = {
  inApp: 'In-app', email: 'Email', sms: 'SMS',
  push: 'Mobile push', webPush: 'Browser push', whatsapp: 'WhatsApp',
};

export const CHANNEL_ICON: Record<string, string> = {
  inApp: 'pi-bell', email: 'pi-envelope', sms: 'pi-comment',
  push: 'pi-mobile', webPush: 'pi-desktop', whatsapp: 'pi-whatsapp',
};

export const PRIORITY_SEVERITY: Record<string, 'danger' | 'warning' | 'info' | 'secondary'> = {
  URGENT: 'danger', HIGH: 'warning', NORMAL: 'info', LOW: 'secondary',
};

/** Per-channel delivery outcome → colour. */
export const STATUS_TONE: Record<string, string> = {
  DELIVERED: 'text-emerald-600 dark:text-emerald-400',
  SENT: 'text-emerald-600 dark:text-emerald-400',
  QUEUED: 'text-amber-600 dark:text-amber-400',
  FAILED: 'text-rose-600 dark:text-rose-400',
};

export const STATUS_TAG: Record<string, 'success' | 'warning' | 'danger' | 'secondary'> = {
  DELIVERED: 'success', SENT: 'success', QUEUED: 'warning', FAILED: 'danger',
};

/**
 * Header treatment for one channel's block on the detail page.
 *
 * With every channel expanded the blocks need separating, and the thing worth
 * separating them BY is the delivery outcome — so the band is tinted by status
 * rather than by which channel it is. A failed send is then visible from the
 * top of the page without reading a word.
 */
export interface StatusBand { bar: string; bg: string; icon: string }

export const STATUS_BAND: Record<string, StatusBand> = {
  DELIVERED: {
    bar: 'border-emerald-500',
    bg: 'bg-emerald-50 dark:bg-emerald-950/30',
    icon: 'text-emerald-600 dark:text-emerald-400',
  },
  SENT: {
    bar: 'border-emerald-500',
    bg: 'bg-emerald-50 dark:bg-emerald-950/30',
    icon: 'text-emerald-600 dark:text-emerald-400',
  },
  QUEUED: {
    bar: 'border-amber-500',
    bg: 'bg-amber-50 dark:bg-amber-950/30',
    icon: 'text-amber-600 dark:text-amber-400',
  },
  FAILED: {
    bar: 'border-rose-500',
    bg: 'bg-rose-50 dark:bg-rose-950/30',
    icon: 'text-rose-600 dark:text-rose-400',
  },
};

export const STATUS_BAND_DEFAULT: StatusBand = {
  bar: 'border-gray-300 dark:border-gray-600',
  bg: 'bg-canvas dark:bg-gray-900/40',
  icon: 'text-gray-400',
};

/**
 * The pictures a notification carried, in the order its template named them.
 *
 * The engine answers with `images`; `image` is the first of them, kept for callers that
 * want one. A payload that named `imageUrl` or `imageUrls` is the fallback, which is what
 * fires from before the engine exposed the rendered picture carry. Either may arrive as
 * one URL or an array, so both shapes are read.
 *
 * Shared so the bell and the inbox cannot disagree about what a notification shows.
 */
export const picturesOf = (n: Notification): string[] => {
  const data = (n.data as Record<string, unknown> | null) || {};
  const candidates = [n.images, n.image, data.imageUrls, data.imageUrl];
  const found: string[] = [];
  candidates.forEach((candidate) => {
    (Array.isArray(candidate) ? candidate : [candidate]).forEach((url) => {
      if (typeof url === 'string' && url.trim() && !found.includes(url)) found.push(url);
    });
  });
  return found;
};

export const stripHtml = (html?: string) =>
  (html || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ').trim();

export const chansOf = (n: Notification) => (n.channels as Record<string, any>) || {};

/**
 * The channels a notification went out on.
 *
 * An operator's payload carries the full `channels` map (status, error,
 * attempts); a recipient's carries only `deliveryChannels`, a list of names.
 * Both audiences may see WHICH channels were used, so read the map when it is
 * there and fall back to the list when it is not.
 */
export const channelNamesOf = (n: Notification): string[] => {
  const keys = Object.keys(chansOf(n));
  if (keys.length) return keys;
  const list = (n as Record<string, unknown>).deliveryChannels;
  return Array.isArray(list) ? (list as string[]).filter((c) => typeof c === 'string') : [];
};

/** Worst outcome across a notification's channels — what the inbox row reports. */
export const rollupStatus = (n: Notification): string => {
  const all = Object.values(chansOf(n)).map((v: any) => String(v?.status || '').toUpperCase());
  if (all.some((s) => s === 'FAILED')) return 'FAILED';
  if (all.some((s) => s === 'QUEUED')) return 'QUEUED';
  if (all.some((s) => s === 'SENT')) return 'SENT';
  if (all.some((s) => s === 'DELIVERED')) return 'DELIVERED';
  return '—';
};
